import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Shield } from 'lucide-react';

import { ApiError, permissionsCatalog, tenantApi } from '@/features/auth/api';
import { SmartLoading } from '@/lib/ui';
import { useAuthStore } from '@/stores/auth-store';

// Fallback list shown when the tenant's role catalog hasn't been seeded yet
// (fresh signups, pre-migration tenants). Without this the "Source role"
// dropdown in the clone modal renders empty and the action becomes a no-op.
const SYSTEM_ROLE_FALLBACK = [
  { slug: 'super_admin', name: 'Super Admin' },
  { slug: 'ops_manager', name: 'Operations Manager' },
  { slug: 'hr_manager', name: 'HR Manager' },
  { slug: 'field_user', name: 'Field User' },
];

type Role = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  permissions: string[];
  is_system_default: boolean;
  cloned_from: string | null;
};

export function RolesPage() {
  const queryClient = useQueryClient();
  const perms = useAuthStore.getState().tenant?.permissions ?? [];
  const canCreate = perms.includes('role.create');
  const canEdit = perms.includes('role.update');
  const canDelete = perms.includes('role.delete');

  const roles = useQuery({ queryKey: ['tenant', 'roles'], queryFn: tenantApi.roles });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);
  const [creating, setCreating] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenant', 'roles'] });

  const data = (roles.data ?? []) as Role[];
  const systemRoles = data.filter((r) => r.is_system_default);
  const sourceForClone =
    systemRoles.length > 0
      ? systemRoles.map((r) => ({ slug: r.slug, name: r.name }))
      : SYSTEM_ROLE_FALLBACK;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2.5">
            <Shield className="h-6 w-6 text-primary" />
            Roles
          </h1>
          <p className="page-subtitle">
            System roles + any custom roles you've created. System defaults can be cloned but not
            edited directly.
          </p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <button onClick={() => setCloning(true)} className="btn-secondary">
              <Copy className="h-4 w-4" />
              Clone system role
            </button>
          )}
          {canCreate && (
            <button onClick={() => setCreating(true)} className="btn-primary">
              <Plus className="h-4 w-4" />
              New role
            </button>
          )}
        </div>
      </header>

      {roles.isLoading && (
        <SmartLoading label="Loading roles…" className="text-sm text-muted-foreground" />
      )}

      <div className="space-y-3">
        {data.map((r) => (
          <div key={r.id} className="card">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium">{r.name}</h3>
                <p className="text-xs text-muted-foreground">
                  {r.is_system_default ? 'System default' : 'Custom'} · {r.slug}
                </p>
                {r.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{r.description}</p>
                )}
              </div>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {r.permissions.length} permissions
              </span>
            </div>
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-muted-foreground">View permissions</summary>
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs">
                {r.permissions.map((p) => (
                  <span key={p} className="rounded bg-muted px-2 py-1">
                    {p}
                  </span>
                ))}
              </div>
            </details>
            {!r.is_system_default && (
              <div className="mt-3 flex gap-3 text-sm">
                {canEdit && (
                  <button
                    onClick={() => setEditingId(r.id)}
                    className="text-primary hover:underline"
                  >
                    Edit
                  </button>
                )}
                {canDelete && <DeleteRoleButton roleId={r.id} onDeleted={invalidate} />}
              </div>
            )}
          </div>
        ))}
      </div>

      {cloning && (
        <CloneRoleModal
          systemRoles={sourceForClone}
          onClose={() => setCloning(false)}
          onCloned={() => {
            setCloning(false);
            invalidate();
          }}
        />
      )}
      {creating && (
        <CreateRoleModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}
      {editingId && (
        <EditRoleModal
          role={data.find((r) => r.id === editingId)!}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function DeleteRoleButton({ roleId, onDeleted }: { roleId: string; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: () => tenantApi.deleteRole(roleId),
    onSuccess: () => {
      setConfirming(false);
      onDeleted();
    },
  });
  if (!confirming) {
    return (
      <button onClick={() => setConfirming(true)} className="text-danger hover:underline">
        Delete
      </button>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <span className="text-muted-foreground">Are you sure?</span>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="text-danger hover:underline"
      >
        {mutation.isPending ? 'Deleting…' : 'Delete'}
      </button>
      <button
        onClick={() => setConfirming(false)}
        className="text-muted-foreground hover:underline"
      >
        Cancel
      </button>
    </span>
  );
}

function PermissionChecklist({
  selected,
  onChange,
}: {
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, typeof permissionsCatalog>();
    for (const p of permissionsCatalog) {
      if (!m.has(p.group)) m.set(p.group, []);
      m.get(p.group)!.push(p);
    }
    return Array.from(m.entries());
  }, []);

  const toggle = (code: string) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(next);
  };

  return (
    <div className="max-h-72 space-y-3 overflow-y-auto rounded border border-border p-3">
      {groups.map(([group, items]) => (
        <div key={group}>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group}
          </div>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {items.map((p) => (
              <label
                key={p.code}
                className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={selected.has(p.code)}
                  onChange={() => toggle(p.code)}
                />
                <span title={p.code}>{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateRoleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      tenantApi.createRole({
        slug,
        name,
        description: description || undefined,
        permissions: Array.from(selected),
      }),
    onSuccess: onCreated,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create role'),
  });

  // Auto-derive a slug from the display name until the user edits it.
  const [slugDirty, setSlugDirty] = useState(false);
  const onNameChange = (v: string) => {
    setName(v);
    if (!slugDirty) {
      setSlug(
        v
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 64),
      );
    }
  };

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-2xl">
        <h2 className="text-lg font-semibold">New role</h2>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (selected.size === 0) {
              setError('Pick at least one permission.');
              return;
            }
            mutation.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Display name</label>
              <input
                className="input mt-1"
                required
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Dispatch Lead"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Slug</label>
              <input
                className="input mt-1 font-mono"
                required
                pattern="[a-z0-9_-]+"
                value={slug}
                onChange={(e) => {
                  setSlugDirty(true);
                  setSlug(e.target.value);
                }}
                placeholder="dispatch_lead"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium">Description</label>
            <textarea
              rows={2}
              className="input mt-1"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Permissions ({selected.size})</label>
            <PermissionChecklist selected={selected} onChange={setSelected} />
          </div>
          {error && (
            <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? 'Creating…' : 'Create role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CloneRoleModal({
  systemRoles,
  onClose,
  onCloned,
}: {
  systemRoles: Array<{ slug: string; name: string }>;
  onClose: () => void;
  onCloned: () => void;
}) {
  const [source, setSource] = useState(systemRoles[0]?.slug ?? '');
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => tenantApi.cloneRole(source, newSlug, newName),
    onSuccess: onCloned,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Clone failed'),
  });

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-md">
        <h2 className="text-lg font-semibold">Clone a system role</h2>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!source) {
              setError('Pick a source role to clone from.');
              return;
            }
            mutation.mutate();
          }}
        >
          <div>
            <label className="block text-sm font-medium">Source role</label>
            <select
              className="input mt-1"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              {systemRoles.length === 0 ? (
                <option value="">No system roles available</option>
              ) : (
                systemRoles.map((r) => (
                  <option key={r.slug} value={r.slug}>
                    {r.name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">New role slug</label>
            <input
              className="input mt-1 font-mono"
              required
              pattern="[a-z0-9_-]+"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Display name</label>
            <input
              className="input mt-1"
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          {error && (
            <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending || !source} className="btn-primary">
              {mutation.isPending ? 'Cloning…' : 'Clone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditRoleModal({
  role,
  onClose,
  onSaved,
}: {
  role: Role;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(role.permissions));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      tenantApi.updateRole(role.id, {
        name,
        description,
        permissions: Array.from(selected),
      }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-2xl">
        <h2 className="text-lg font-semibold">Edit role</h2>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          <div>
            <label className="block text-sm font-medium">Name</label>
            <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium">Description</label>
            <textarea
              rows={2}
              className="input mt-1"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Permissions ({selected.size})</label>
            <PermissionChecklist selected={selected} onChange={setSelected} />
          </div>
          {error && (
            <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
